import { useMemo, useState } from "react";
import type { DragEvent } from "react";
import { Link } from "react-router";
import { Icon } from "@astrum/ui";
import type { Task } from "../../lib/api.js";
import {
  bucketByDay,
  cellOverflow,
  dayKey,
  dayParts,
  monthWeeks,
  rescheduleOnDrop,
  taskSpan,
  todayDay,
} from "../../lib/work-views.js";
import type { TaskViewProps } from "./types.js";
import "./task-views.css";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MAX_PER_CELL = 3;

/** `YYYY-MM` -> [year, monthIndex]; anything else is the current month. */
export function parseMonthKey(value: string | undefined, now = new Date()): [number, number] {
  const match = value ? /^(\d{4})-(\d{2})$/.exec(value) : null;
  if (match && Number(match[2]) >= 1 && Number(match[2]) <= 12) return [Number(match[1]), Number(match[2]) - 1];
  return [now.getFullYear(), now.getMonth()];
}

export const monthKey = (year: number, month: number) => `${year}-${String(month + 1).padStart(2, "0")}`;

interface CalendarProps extends Pick<TaskViewProps, "tasks" | "canEdit" | "onPatch"> {
  /** `YYYY-MM`, from the URL. */
  month?: string;
  onMonthChange: (month: string | undefined) => void;
}

/**
 * A month grid of tasks by due date. A task with a start and a due date appears
 * on every day between them. Editors can drag a task to another day to
 * reschedule it; a task with both dates keeps its length.
 *
 * A cell shows a few tasks and then "+N more", which opens the rest in place —
 * the cell grows rather than hiding work behind a popup.
 */
export function CalendarView({ tasks, canEdit, onPatch, month, onMonthChange }: CalendarProps) {
  const [year, monthIndex] = parseMonthKey(month);
  const weeks = useMemo(() => monthWeeks(year, monthIndex), [year, monthIndex]);
  const from = weeks[0]![0]!;
  const to = weeks[weeks.length - 1]![6]!;
  const buckets = useMemo(() => bucketByDay(tasks, from, to), [tasks, from, to]);
  const undated = useMemo(() => tasks.filter((t) => !taskSpan(t)).length, [tasks]);
  const today = todayDay();

  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [over, setOver] = useState<number | null>(null);

  const go = (delta: number) => {
    const d = new Date(Date.UTC(year, monthIndex + delta, 1));
    onMonthChange(monthKey(d.getUTCFullYear(), d.getUTCMonth()));
    setExpanded(new Set());
  };

  const onDrop = (event: DragEvent, day: number) => {
    event.preventDefault();
    setOver(null);
    const raw = event.dataTransfer.getData("application/x-astrum-task");
    if (!raw) return;
    const { taskId, grabbedDay } = JSON.parse(raw) as { taskId: string; grabbedDay: number };
    const task = tasks.find((t) => t.id === taskId);
    if (!task || !canEdit(task)) return;
    const patch = rescheduleOnDrop(task, grabbedDay, day);
    if (patch) onPatch(task, patch);
  };

  return (
    <div className="tv-calendar">
      <div className="tv-toolbar">
        <button type="button" className="tv-btn tv-btn--icon" aria-label="Previous month" onClick={() => go(-1)}>
          <Icon name="chevron-left" size={14} />
        </button>
        <button type="button" className="tv-btn tv-btn--icon" aria-label="Next month" onClick={() => go(1)}>
          <Icon name="chevron-right" size={14} />
        </button>
        <h3 className="tv-calendar__title" aria-live="polite">
          {MONTH_NAMES[monthIndex]} {year}
        </h3>
        <button type="button" className="tv-btn" onClick={() => onMonthChange(undefined)}>
          Today
        </button>
        <span className="tv-toolbar__count">
          {undated > 0 ? `${undated} without dates` : ""}
        </span>
      </div>

      <div className="tv-calendar__grid" role="grid" aria-label={`${MONTH_NAMES[monthIndex]} ${year}`}>
        <div className="tv-calendar__weekdays" role="row">
          {WEEKDAYS.map((d) => (
            <div key={d} role="columnheader">
              {d}
            </div>
          ))}
        </div>
        {weeks.map((week) => (
          <div key={week[0]} className="tv-calendar__week" role="row">
            {week.map((day) => {
              const entries = buckets.get(day) ?? [];
              const isOpen = expanded.has(day);
              const { shown, more } = cellOverflow(entries, isOpen ? entries.length : MAX_PER_CELL);
              const parts = dayParts(day);
              const outside = parts.month !== monthIndex;
              return (
                <div
                  key={day}
                  role="gridcell"
                  className={[
                    "tv-day",
                    outside ? "is-outside" : "",
                    day === today ? "is-today" : "",
                    over === day ? "is-over" : "",
                  ].join(" ")}
                  data-day={dayKey(day)}
                  onDragOver={(e) => {
                    if (e.dataTransfer.types.includes("application/x-astrum-task")) {
                      e.preventDefault();
                      setOver(day);
                    }
                  }}
                  onDragLeave={() => setOver((cur) => (cur === day ? null : cur))}
                  onDrop={(e) => onDrop(e, day)}
                >
                  <span className="tv-day__num">{parts.date === 1 ? `${MONTH_NAMES[parts.month]!.slice(0, 3)} 1` : parts.date}</span>
                  {shown.map((task) => (
                    <CalendarChip key={task.id} task={task} day={day} editable={canEdit(task)} />
                  ))}
                  {more > 0 && (
                    <button
                      type="button"
                      className="tv-more"
                      onClick={() => setExpanded((prev) => new Set(prev).add(day))}
                    >
                      +{more} more
                    </button>
                  )}
                  {isOpen && entries.length > MAX_PER_CELL && (
                    <button
                      type="button"
                      className="tv-more"
                      onClick={() =>
                        setExpanded((prev) => {
                          const next = new Set(prev);
                          next.delete(day);
                          return next;
                        })
                      }
                    >
                      Show less
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function CalendarChip({ task, day, editable }: { task: Task; day: number; editable: boolean }) {
  const span = taskSpan(task)!;
  const first = day === span.start;
  const last = day === span.end;
  const multi = span.end > span.start;
  // The title repeats on a span's first day and each Sunday, so a week row reads on its own.
  const showTitle = first || !multi || dayParts(day).weekday === 0;
  return (
    <div
      className={[
        "tv-chip",
        multi ? "is-span" : "",
        first ? "is-first" : "",
        last ? "is-last" : "",
        task.status === "done" ? "is-done" : "",
        editable ? "is-draggable" : "",
      ].join(" ")}
      draggable={editable}
      onDragStart={(e) => {
        e.dataTransfer.setData("application/x-astrum-task", JSON.stringify({ taskId: task.id, grabbedDay: day }));
        e.dataTransfer.effectAllowed = "move";
      }}
    >
      <Link to={`/work/task/${task.id}`} title={task.title} draggable={false} tabIndex={showTitle ? 0 : -1}>
        {showTitle ? task.title : " "}
      </Link>
    </div>
  );
}
